import React, { useState, useEffect, useRef } from "react";
import styles from "./Templates.module.scss";
import TemplateList from "./TemplateList";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import queryString from "query-string";
import { isEmpty } from "lodash";
import TemplateEditor from "./TemplateEditor";
import { Button, Modal, Box } from "@mui/material";
import CreateConsentTemplateForm from "./CreateConsentTemplateForm";
import TemplatesComponent from "../TemplateModel";
import Header from "../NewUi/Header/header";

import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import ConsentGalleryPanel from "../NewUi/TreatmentFlow/ConsentGalleryPanel";

import { HiSparkles } from "react-icons/hi2";
import { toast } from "react-toastify";
import ImageGalleryV2 from "../../components/ImageGallery/imageGalleryV3";

declare global {
  interface Window {
    CKEDITOR: any;
  }
}

const AI_EDITOR_ID = "ai-editor";

const Templates = () => {
  const editorRef = useRef<HTMLTextAreaElement | null>(null);

  const [templates, setTemplates] = useState<any[]>([]);
  const [nonDefaultTemplates, setNonDefaultTemplates] = useState<any[]>([]);
  const [globaleTemplates, setGlobalTemplates] = useState<any[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [category, setCategory] = useState("All");
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [dropdownClicked, setDropdownClicked] = useState(false);
  const [isOpenCreateConsentModal, setIsOpenCreateConsentModal] = useState(false);
  const [treatmentPlans, setTreatmentPlans] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isWorkflowMinimized, setIsWorkflowMinimized] = useState(false);

  const [generatorTab, setGeneratorTab] = useState<"manual" | "ai">("manual");

  const [editTemplate, setEditTemplate] = useState<any>(null);
  const [selectedTreatments, setSelectedTreatments] = useState<number[]>([]);
  const [treatmentSearch, setTreatmentSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [showAiSaveConfirm, setShowAiSaveConfirm] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const [generatorPrompt, setGeneratorPrompt] = useState("");
  const [generatingTemplate, setGeneratingTemplate] = useState(false);

  const [showGallery, setShowGallery] = useState(false);
  const [activeEditorId, setActiveEditorId] = useState(AI_EDITOR_ID);
  const [preview, setPreview] = useState<any>(null);
  const [editorLoading, setEditorLoading] = useState(false);

  const [highlightedTemplateId, setHighlightedTemplateId] = useState<number | null>(null);

  const getToken = () =>
    axios.defaults.headers.common.Authorization?.replace("Bearer ", "") ||
    localStorage.getItem("token");

const getTreatmentLabel = (t: any) =>
    t.description || t.name || t.category_name || `Treatment ${t.id}`;

  const countWords = (text: string) =>
    text.trim().split(/\s+/).filter(Boolean).length;

  const uploadCallback = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    return axios.post("/uploads/image", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  };

  const destroyEditorInstance = (editorId: string) => {
  const instance = window.CKEDITOR?.instances?.[editorId];
  if (!instance) return;

  try {
    instance.destroy(true);
  } catch (err) {
    console.error(`Failed to destroy CKEditor instance: ${editorId}`, err);
  }
};

const destroyAiEditor = () => {
  destroyEditorInstance(AI_EDITOR_ID);
};
  useQuery(
    ["isAdmin"],
    async () => {
      const { data } = await axios.get("/user/is_admin");
      return data;
    }
  );

  useEffect(() => {
    const fetchTreatmentPlans = async () => {
      try {
        const { data } = await axios.get(`/treatment`);
        const plans = data.treatments.map((plan: any) => ({
          ...plan,
          label: plan.description,
          value: plan.id,
        }));
        setTreatmentPlans(plans);
      } catch (error) {
        console.error("Error fetching treatment plans:", error);
      }
    };

    fetchTreatmentPlans();
  }, []);

  const { data: categoryData, isLoading: isCategoryLoading } = useQuery(
    ["categories"],
    async () => {
      const { data } = await axios.post(`/treatment_category`);
      return data;
    },
    {
      enabled: dropdownClicked,
    }
  );

  const { isLoading, refetch } = useQuery(
    ["consentClauses", categoryId, search],
    async () => {
      const params = { category: categoryId || undefined, search };
      const stringified = queryString.stringify(params);
      const { data } = await axios.get(`/consent_clause?${stringified}`);

      setTemplates(data.consent_form_templates || []);
      setNonDefaultTemplates(data.non_default_consent_form_templates || []);
      setGlobalTemplates(data.global_templates || []);

      return data;
    },
    {
      enabled: true,
      onSuccess: (data) => {
        if (isEmpty(selectedTemplate) && data?.consent_form_templates?.length) {
         
        }
      },
    }
  );

  useEffect(() => {
  if (!editTemplate) return;

  let cancelled = false;
  let initTimeout: ReturnType<typeof setTimeout> | null = null;

  const loadCkeditorScript = () =>
    new Promise<void>((resolve, reject) => {
      if (window.CKEDITOR) {
        resolve();
        return;
      }

      const existingScript = document.querySelector(
        'script[src="/ckeditor/ckeditor.js"]'
      ) as HTMLScriptElement | null;

      if (existingScript) {
        existingScript.addEventListener("load", () => resolve(), { once: true });
        existingScript.addEventListener(
          "error",
          () => reject(new Error("Failed to load CKEditor script")),
          { once: true }
        );
        return;
      }

      const script = document.createElement("script");
      script.src = "/ckeditor/ckeditor.js";
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load CKEditor script"));
      document.body.appendChild(script);
    });

  const initEditor = async () => {
    try {
      setEditorLoading(true);

      await loadCkeditorScript();
      if (cancelled) return;

      initTimeout = setTimeout(() => {
        if (cancelled || !editorRef.current || !window.CKEDITOR) return;

        destroyAiEditor();

        editorRef.current.id = AI_EDITOR_ID;

        const editor = window.CKEDITOR.replace(AI_EDITOR_ID, {
          height: 700,
          extraPlugins:
            "placeholder_select,simpleImageUpload,image2,image,signaturebuttons,gallerybutton,uploadimage",
          toolbar: [
            { name: "basicstyles", items: ["Bold", "Italic", "Underline", "Strike"] },
            {
              name: "paragraph",
              items: ["NumberedList", "BulletedList", "Outdent", "Indent", "Blockquote"],
            },
            { name: "links", items: ["Link", "Unlink"] },
            { name: "styles", items: ["Format", "Font", "FontSize"] },
            { name: "colors", items: ["TextColor", "BGColor"] },
            {
              name: "insert",
              items: [
                "GalleryButton",
                "SignatureMandatory",
                "SignatureOptional",
                "ClinicianSignature",
              ],
            },
          ],
          allowedContent: true,
          image2_disableResizer: false,
          image_prefillDimensions: true,
        });

        editor.on("instanceReady", () => {
          if (cancelled) return;
          editor.setData(editTemplate.body || "");
          setEditorLoading(false);
          setActiveEditorId(editor.name || AI_EDITOR_ID);
        });

        editor.on("dataReady", () => {
          if (cancelled) return;
          setEditorLoading(false);
        });
      }, 80);
    } catch (err) {
      console.error("CKEditor failed to initialize:", err);
      setEditorLoading(false);
    }
  };

  initEditor();

  return () => {
    cancelled = true;
    if (initTimeout) clearTimeout(initTimeout);
    destroyAiEditor();
    setEditorLoading(false);
  };
}, [editTemplate?.id]);

  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 10000);

    return () => clearInterval(interval);
  }, [refetch]);

  
  const handleSelect = (template: any) => {
    setHighlightedTemplateId(null);
    setSelectedTemplate(template);
    setCategory(template?.category_name || "All");
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCategory = e.target.value;
    setCategory(selectedCategory);

    const selectedCategoryData = categoryData?.find(
      (cat: any) => cat.category_name === selectedCategory
    );

    setCategoryId(selectedCategoryData ? selectedCategoryData.id : null);
  };

  const handleDropdownClick = () => {
    setDropdownClicked(true);
  };

  const handleCreateNewTemplate = async (newTemplateId: number) => {
    const { data } = await axios.get(`/consent_clause?=`);
    const newTemplate = [
      ...(data?.consent_form_templates || []),
      ...(data?.non_default_consent_form_templates || []),
    ].find((template: any) => template.id === newTemplateId);

    if (newTemplate) {
      setSelectedTemplate(newTemplate);
      setTemplates(data.consent_form_templates || []);
      setNonDefaultTemplates(data.non_default_consent_form_templates || []);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleGenerateTemplate = async () => {
    const trimmedPrompt = generatorPrompt.trim();
    const wordCount = countWords(trimmedPrompt);

    if (!trimmedPrompt) {
      toast.error("Please enter a treatment prompt.");
      return;
    }

    if (wordCount > 20) {
      toast.error("Prompt must be 20 words or fewer.");
      return;
    }

    try {
      setGeneratingTemplate(true);

      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL_V2}template_generator/generate-template`,
        {
          treatment_item: trimmedPrompt,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      const generatedHtml =
        res.data?.html ||
        res.data?.body ||
        res.data?.template ||
        res.data?.data?.html ||
        res.data;

      if (typeof generatedHtml !== "string" || !generatedHtml.trim()) {
        throw new Error("Template generator returned an invalid HTML response.");
      }

      setSelectedTreatments([]);
      setTreatmentSearch("");
      setDropdownOpen(false);

      setEditTemplate({
        id: 0,
        name: trimmedPrompt,
        body: generatedHtml,
        level: "user",
        treatment_ids: [],
        category_id: null,
      });

      toast.success("Template generated successfully");
    } catch (err: any) {
      console.error(err);

      const backendMessage =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Failed to generate template";

      toast.error(
        typeof backendMessage === "string"
          ? backendMessage
          : JSON.stringify(backendMessage)
      );
    } finally {
      setGeneratingTemplate(false);
    }
  };


  const saveTemplate = async () => {
  if (!editTemplate) return;

  try {
    setSavingTemplate(true);

    const editorContent =
      window.CKEDITOR?.instances?.[AI_EDITOR_ID]?.getData?.() ??
      editTemplate.body ??
      "";

    let updatedContent = editorContent;

    const base64Regex = /<img[^>]+src="(data:image\/[^;]+;base64,[^"]+)"[^>]*>/g;
    const matches = [...editorContent.matchAll(base64Regex)];

    for (const match of matches) {
      const base64Data = match[1];
      if (!base64Data) continue;

      const blob = await fetch(base64Data).then((res) => res.blob());
      const file = new File([blob], "upload.png", { type: blob.type });
      const uploadRes = await uploadCallback(file);

      const uploadedUrl =
        uploadRes?.data?.url ||
        uploadRes?.data?.data?.url ||
        uploadRes?.data?.path ||
        uploadRes?.data;

      if (typeof uploadedUrl === "string") {
        updatedContent = updatedContent.replace(base64Data, uploadedUrl);
      }
    }

    const token = getToken();

    const payload = {
      name: editTemplate.name,
      body: updatedContent,
      level: "user",
      is_default: false,
      is_cloned: false,
      parent_id: null,
      treatment_id: selectedTreatments.length > 0 ? selectedTreatments[0] : null,
      treatment_ids: selectedTreatments,
      category_id: editTemplate.category_id ?? null,
    };

    const saveRes = await axios.post(
      `${process.env.REACT_APP_BASE_URL}consent_clause`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    console.log("saveRes:", saveRes);
    console.log("saveRes.data:", saveRes?.data);

    const createdTemplateId =
      saveRes?.data?.id ||
      saveRes?.data?.data?.id ||
      saveRes?.data?.consent_clause_id;

    const latestRes = await axios.get(`/consent_clause?=`);

    setTemplates(latestRes?.data?.consent_form_templates || []);
    setNonDefaultTemplates(latestRes?.data?.non_default_consent_form_templates || []);
    setGlobalTemplates(latestRes?.data?.global_templates || []);

    setHighlightedTemplateId(createdTemplateId ?? null);

    toast.success("Template saved successfully");

    handleBackToManual();
  } catch (err: any) {
    console.error("saveTemplate error:", err);
    console.error("saveTemplate error response:", err?.response);
    console.error("saveTemplate error response data:", err?.response?.data);

    const backendMessage =
      err?.response?.data?.detail ||
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      "Failed to save template";

    toast.error(
      typeof backendMessage === "string"
        ? backendMessage
        : JSON.stringify(backendMessage)
    );
  } finally {
    setSavingTemplate(false);
  }
};

 const handleBackToManual = () => {
  setShowAiSaveConfirm(false);
  setPreview(null);
  destroyAiEditor();
  setEditTemplate(null);
  setSelectedTreatments([]);
  setSelectedTemplate(null);
  setTreatmentSearch("");
  setDropdownOpen(false);
  setGeneratorPrompt("");
  setGeneratorTab("manual");
};

  const handleSave = async () => {
    if (!editTemplate) return;
    setShowAiSaveConfirm(true);
  };

  const filteredTreatments = treatmentPlans.filter((t: any) => {
    const label = getTreatmentLabel(t).toLowerCase();
    return (
      label.includes(treatmentSearch.toLowerCase()) &&
      !selectedTreatments.includes(t.id)
    );
  });

  const generatorWordCount = countWords(generatorPrompt);
  const isGeneratorPromptTooLong = generatorWordCount > 20;


useEffect(() => {
  const openGalleryHandler = (event: Event) => {
    const customEvent = event as CustomEvent<{ editorId?: string }>;
    const editorId = customEvent.detail?.editorId || AI_EDITOR_ID;

    setActiveEditorId(editorId);
    setShowGallery(true);
  };

  window.addEventListener("ckeditor-open-gallery", openGalleryHandler);

  return () => {
    window.removeEventListener("ckeditor-open-gallery", openGalleryHandler);
  };
}, []);
 
  return (
    <>
      <Header />

      <div className={styles.templatesPage}>
        <aside className={styles.sidePanel}>
          <div className={styles.sidebarHeader}>
            <p className={styles.pageTag}>Consent Management</p>
            <h1 className={styles.header}>My Consent Templates</h1>
            <p className={styles.sidebarSubtext}>
              Search, filter, and manage your consent clauses in one place.
            </p>
          </div>

          <div className={styles.searchFilter}>
            <input
              type="text"
              placeholder="Search templates..."
              className={styles.searchInput}
              value={search}
              onChange={handleSearchChange}
            />

            {/* <select
              className={styles.categoryFilter}
              value={category}
              onChange={handleCategoryChange}
              onClick={handleDropdownClick}
            >
              <option value="All">All</option>
              {isCategoryLoading ? (
                <option disabled>Loading categories...</option>
              ) : categoryData && Array.isArray(categoryData) ? (
                categoryData.map((cat: any) => (
                  <option key={cat.id} value={cat.category_name}>
                    {cat.category_name}
                  </option>
                ))
              ) : (
                <option disabled>No categories available</option>
              )}
            </select> */}
          </div>

          <Modal open={isModalOpen} onClose={closeModal}>
            <Box>
              <TemplatesComponent closeModal={closeModal} />
            </Box>
          </Modal>

          <div className={styles.listWrapper}>
            <TemplateList
              defaultTemplates={templates}
              nonDefaultTemplates={nonDefaultTemplates}
              globalTemplates={globaleTemplates}
              onSelect={handleSelect}
              isLoading={isLoading}
              selectedTemplateId={highlightedTemplateId ?? (selectedTemplate ? selectedTemplate.id : null)}
            />
          </div>
        </aside>


        <main className={styles.mainContent}>

          {/* <div className={styles.generatorTabsWrap}>
            
            <button
              type="button"
              onClick={() => {
                setGeneratorTab("manual");
                setIsOpenCreateConsentModal(true);
              }}
              className={`${styles.generatorTab} ${
                generatorTab === "manual" ? styles.generatorTabActive : ""
              }`}
            >
              <EditOutlinedIcon className={styles.generatorTabIcon} />
               + Create New Consent Clause
            </button>

            <button
              type="button"
              onClick={() => setGeneratorTab("ai")}
              className={`${styles.generatorTab} ${
                generatorTab === "ai" ? styles.generatorTabActive : ""
              }`}
            >
              <SmartToyOutlinedIcon className={styles.generatorTabIcon} />
              AI Consent Generator
            </button>
          </div> */}

          <div className={styles.topActions}>
            
            <div className={styles.heroContent}>
              
              <p className={styles.pageTag}>Editor Workspace</p>
              <h2 className={styles.heroTitle}>Consent Clause Builder</h2>
              <p className={styles.heroText}>
                Create, review, and update your clause templates with a cleaner
                editing workflow.
              </p>
            </div>

        
           <div className={styles.actionButtons}>
  {generatorTab === "manual" ? (
    <>
      <Button
        type="button"
        variant="contained"
        onClick={() => setIsOpenCreateConsentModal(true)}
        className={styles.createButton}
      >
        <span className={styles.createButtonInner}>
          <span className={styles.createButtonText}>
            + Create New Consent Clause
          </span>
        </span>
      </Button>

      <Button
        type="button"
        variant="contained"
        onClick={() => setGeneratorTab("ai")}
        className={`${styles.generateTemplateBtn} ${
          generatorTab === "ai" ? styles.generateTemplateBtnActive : ""
        }`}
      >
        <span className={styles.generateTemplateInner}>
          <span className={styles.generateTemplateGlow} />
          <AutoAwesomeRoundedIcon className={styles.generateTemplateIcon} />
          <span className={styles.generateTemplateText}>
            Generate Consent Template with AI
          </span>
        </span>
      </Button>
    </>
  ) : (
    <>
      <Button
  type="button"
  variant="contained"
  onClick={handleBackToManual}
  className={styles.backToManualTopButton}
>
  ← Back
</Button>

      <Button
        type="button"
        variant="contained"
        onClick={() => setGeneratorTab("ai")}
        className={`${styles.generateTemplateBtn} ${
          generatorTab === "ai" ? styles.generateTemplateBtnActive : ""
        }`}
      >
        <span className={styles.generateTemplateInner}>
          <span className={styles.generateTemplateGlow} />
          <AutoAwesomeRoundedIcon className={styles.generateTemplateIcon} />
          <span className={styles.generateTemplateText}>
            Generate Consent Template with AI
          </span>
        </span>
      </Button>
    </>
  )}
</div>
          </div>

          

          {generatorTab === "manual" ? (
            <>
              {!isWorkflowMinimized ? (
                <div className={styles.banner}>
                  <div className={styles.bannerGlow} />

                  <div className={styles.bannerHeaderBar}>
                    <div className={styles.workflowHeader}>
                      <p className={styles.workflowEyebrow}>Consent Builder</p>
                      <h3 className={styles.workflowTitle}>
                        My Consent Templates <span>Workflow</span>
                      </h3>
                      <p className={styles.workflowSubtitle}>
                        Create, map, edit, and save your consent clauses through a clean guided flow.
                      </p>
                    </div>

                    <button
                      type="button"
                      className={styles.minimizeBannerButton}
                      onClick={() => setIsWorkflowMinimized(true)}
                      aria-label="Minimize workflow banner"
                      title="Minimize"
                    >
                      <KeyboardArrowUpRoundedIcon />
                    </button>
                  </div>

                  <div className={styles.workflowTrack}>
                    <div className={styles.workflowStep}>
                      <div className={styles.stepTopSpacer} />
                      <div className={styles.stepCircle}>
                        <NoteAltOutlinedIcon className={styles.stepMuiIcon} />
                      </div>
                      <div className={styles.stepBottom}>
                        <div className={styles.stepCard}>
                          <div className={styles.stepNumber}>01</div>
                          <div className={styles.stepLabel}>Click on Create New Consent Clause</div>
                        </div>
                      </div>
                    </div>

                    <div className={styles.workflowStep}>
                      <div className={styles.stepTop}>
                        <div className={styles.stepCard}>
                          <div className={styles.stepNumberTop}>02</div>
                          <div className={styles.stepLabelTop}>Select a Medicube category</div>
                        </div>
                      </div>
                      <div className={styles.stepCircle}>
                        <CategoryOutlinedIcon className={styles.stepMuiIcon} />
                      </div>
                      <div className={styles.stepBottomEmpty} />
                    </div>

                    <div className={styles.workflowStep}>
                      <div className={styles.stepTopSpacer} />
                      <div className={styles.stepCircle}>
                        <AssignmentTurnedInOutlinedIcon className={styles.stepMuiIcon} />
                      </div>
                      <div className={styles.stepBottom}>
                        <div className={styles.stepCard}>
                          <div className={styles.stepNumber}>03</div>
                          <div className={styles.stepLabel}>Add Dentally Codes</div>
                        </div>
                      </div>
                    </div>

                    <div className={styles.workflowStep}>
                      <div className={styles.stepTop}>
                        <div className={styles.stepCard}>
                          <div className={styles.stepNumberTop}>04</div>
                          <div className={styles.stepLabelTop}>Make Your Edits</div>
                        </div>
                      </div>
                      <div className={styles.stepCircle}>
                        <ModeEditOutlineOutlinedIcon className={styles.stepMuiIcon} />
                      </div>
                      <div className={styles.stepBottomEmpty} />
                    </div>

                    <div className={styles.workflowStep}>
                      <div className={styles.stepTopSpacer} />
                      <div className={styles.stepCircle}>
                        <SaveOutlinedIcon className={styles.stepMuiIcon} />
                      </div>
                      <div className={styles.stepBottom}>
                        <div className={styles.stepCard}>
                          <div className={styles.stepNumber}>05</div>
                          <div className={styles.stepLabel}>Save Your Clause</div>
                        </div>
                      </div>
                    </div>

                    <div className={`${styles.workflowStep} ${styles.finalStep}`}>
                      <div className={styles.stepTopSpacer} />
                      <div className={`${styles.stepCircle} ${styles.stepCircleActive}`}>
                        <TaskAltOutlinedIcon className={styles.stepMuiIcon} />
                      </div>
                      <div className={styles.finalRight}>
                        <div className={`${styles.stepCard} ${styles.finalCard}`}>
                          <div className={styles.stepNumberTop}>06</div>
                          <div className={styles.stepLabelTop}>It&apos;s Ready to Use</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={styles.bannerMiniWrap}>
                  <button
                    type="button"
                    className={styles.bannerMiniButton}
                    onClick={() => setIsWorkflowMinimized(false)}
                    aria-label="Open workflow banner"
                    title="Open workflow"
                  >
                    <AutoAwesomeRoundedIcon className={styles.bannerMiniIcon} />
                    <span className={styles.bannerMiniText}>Workflow</span>
                  </button>
                </div>
              )}

              <div className={styles.editorSection}>
                <TemplateEditor
                  selectedTemplate={selectedTemplate}
                  categoryData={categoryData}
                  onSave={() => {}}
                  refetch={refetch}
                  defaultCategory={category}
                />
              </div>
            </>
          ) : (
            <div className={styles.aiGeneratorPanel}>
              <div className={styles.aiGeneratorHeader}>
                <div>
                
                  <p className={styles.aiEyebrow}>AI Consent Generator</p>
                  <h3 className={styles.aiTitle}>Generate consent templates with AI</h3>
                  <p className={styles.aiSubtitle}>
                    Use AI to draft, refine, and save consent clauses based on category, treatment, and instructions.
                  </p>
                </div>
 
                <div className={styles.aiBadge}>
                {/* <button
  type="button"
  className={styles.backButton}
  onClick={handleBackToManual}
>
  ← Go Back to Manual
</button> */}

                <AutoAwesomeRoundedIcon />
                <span>AI Powered</span>
                <span className={styles.experimentalTag}>Experimental</span>
              </div>
              </div>

              <div className={styles.generateLayout}>
                <div className={styles.generateMainCard}>
                  <div className={styles.generateCardHeader}>
                    <h3>Treatment Prompt</h3>
                    <p>Keep the prompt short and specific. Maximum 20 words.</p>
                  </div>

                  <div className={styles.generateInputWrap}>
                    <textarea
                      className={styles.generateTextarea}
                      rows={5}
                      value={generatorPrompt}
                      onChange={(e) => setGeneratorPrompt(e.target.value)}
                      placeholder="Example: Composite bonding for chipped front tooth repair"
                      disabled={generatingTemplate}
                    />

                    <div className={styles.generateMetaRow}>
                      <div
                        className={`${styles.wordCount} ${
                          isGeneratorPromptTooLong ? styles.wordCountError : ""
                        }`}
                      >
                        {generatorWordCount}/20 words
                      </div>

                      <div className={styles.generateHint}>
                        A short treatment-focused phrase works best.
                      </div>
                    </div>
                  </div>

                  <div className={styles.generateActions}>
                    <button
                      className={styles.generateBtn}
                      onClick={handleGenerateTemplate}
                      disabled={
                        generatingTemplate ||
                        isGeneratorPromptTooLong ||
                        !generatorPrompt.trim()
                      }
                    >
                      {generatingTemplate ? (
                        <span className={styles.generateBtnInner}>
                          <span className={styles.buttonSpinner} />
                          Generating Template...
                        </span>
                      ) : (
                        <span className={styles.generateBtnInner}>
                          <HiSparkles className={styles.aiIcon} />
                          Generate Template
                        </span>
                      )}
                    </button>
                  </div>
                </div>

                <div className={styles.generateSideCard}>
                  <h3>Prompt Tips</h3>

                  <div className={styles.tipList}>
                    <div className={styles.tipItem}>
                      <strong>Be specific</strong>
                      <span>Include the treatment name and short context.</span>
                    </div>

                    <div className={styles.tipItem}>
                      <strong>Keep it short</strong>
                      <span>Do not exceed 20 words.</span>
                    </div>

                    <div className={styles.tipItem}>
                      <strong>Use plain language</strong>
                      <span>Avoid combining too many procedures in one prompt.</span>
                    </div>
                  </div>

                  <div className={styles.exampleBlock}>
                    <div className={styles.exampleTitle}>Examples</div>

                    <button
                      type="button"
                      className={styles.examplePrompt}
                      onClick={() =>
                        !generatingTemplate &&
                        setGeneratorPrompt("Composite bonding for chipped upper front tooth")
                      }
                    >
                      Composite bonding for chipped upper front tooth
                    </button>

                    <button
                      type="button"
                      className={styles.examplePrompt}
                      onClick={() =>
                        !generatingTemplate &&
                        setGeneratorPrompt("Root canal treatment for lower molar with crown advice")
                      }
                    >
                      Root canal treatment for lower molar with crown advice
                    </button>

                    <button
                      type="button"
                      className={styles.examplePrompt}
                      onClick={() =>
                        !generatingTemplate &&
                        setGeneratorPrompt("Simple extraction for painful broken tooth")
                      }
                    >
                      Simple extraction for painful broken tooth
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <CreateConsentTemplateForm
            isOpen={isOpenCreateConsentModal}
            close={() => setIsOpenCreateConsentModal(false)}
            categories={categoryData || []}
            treatmentPlans={treatmentPlans}
            setCurrentTemplate={handleCreateNewTemplate}
          />
        </main>
      </div>

      {editTemplate && (
        <div
          className={styles.modalOverlay}
          onClick={() => {
            destroyAiEditor();
            setEditTemplate(null);
          }}
        >
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Generated Template</h2>
              <button
                className={styles.closeBtn}
                onClick={() => {
                  destroyAiEditor();
                  setEditTemplate(null);
                }}
              >
                ✕
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label className={styles.fieldLabel}>Template Name</label>

                <input
                  type="text"
                  className={styles.nameInput}
                  value={editTemplate.name}
                  onChange={(e) =>
                    setEditTemplate({ ...editTemplate, name: e.target.value })
                  }
                  placeholder="Enter template name"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.fieldLabel}>Select Treatments</label>

                <div className={styles.treatmentPicker}>
                  <div className={styles.treatmentPickerTop}>
                    <div className={styles.treatmentPickerTitle}>Assigned treatments</div>
                    <div className={styles.treatmentPickerCount}>
                      {selectedTreatments.length} selected
                    </div>
                  </div>

                  <div className={styles.multiSelectBox}>
                    <div className={styles.selectedChips}>
                      {selectedTreatments.map((id) => {
                        const treatment = treatmentPlans.find((t: any) => t.id === id);
                        return (
                          <div key={id} className={styles.chip}>
                            <span className={styles.chipText}>
                              {treatment ? getTreatmentLabel(treatment) : `Treatment ${id}`}
                            </span>

                            <span
                              className={styles.removeChip}
                              onClick={() =>
                                setSelectedTreatments((prev) =>
                                  prev.filter((x) => x !== id)
                                )
                              }
                            >
                              ✕
                            </span>
                          </div>
                        );
                      })}

                      <div className={styles.searchSelectWrapper}>
                        <input
                          type="text"
                          placeholder="Search treatments..."
                          className={styles.treatmentSearchInput}
                          value={treatmentSearch}
                          onChange={(e) => {
                            setTreatmentSearch(e.target.value);
                            setDropdownOpen(true);
                          }}
                          onFocus={() => setDropdownOpen(true)}
                        />

                        {dropdownOpen && filteredTreatments.length > 0 && (
                          <div className={styles.dropdownList}>
                            {filteredTreatments.map((t: any) => (
                              <div
                                key={t.id}
                                className={styles.dropdownItem}
                                onClick={() => {
                                  setSelectedTreatments((prev) => [...prev, t.id]);
                                  setTreatmentSearch("");
                                  setDropdownOpen(false);
                                }}
                              >
                                {getTreatmentLabel(t)}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {editorLoading && (
                <div className={styles.editorLoadingState}>Loading editor...</div>
              )}

              <textarea
  key={editTemplate?.id || AI_EDITOR_ID}
  id={AI_EDITOR_ID}
  ref={editorRef}
  className={styles.editorTextarea}
  defaultValue={editTemplate.body}
/>
            </div>

            <div className={styles.modalFooter}>
              <button
                className={styles.viewBtnOutlined}
                onClick={() => {
                  const editorContent =
                    window.CKEDITOR?.instances?.[AI_EDITOR_ID]?.getData?.() ||
                    editTemplate.body;

                  setPreview({
                    ...editTemplate,
                    body: editorContent,
                  });
                }}
              >
                Preview
              </button>

              <button className={styles.saveBtn} onClick={handleSave}>
                Save Template
              </button>
            </div>
          </div>
        </div>
      )}

      {showAiSaveConfirm && (
        <div
          className={styles.modalOverlay}
          onClick={() => !savingTemplate && setShowAiSaveConfirm(false)}
        >
          <div
            className={styles.aiConfirmModal}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.aiConfirmHeader}>
              <h3>Confirm Save</h3>
              <button
                className={styles.closeBtn}
                onClick={() => !savingTemplate && setShowAiSaveConfirm(false)}
                disabled={savingTemplate}
              >
                ✕
              </button>
            </div>

            <div className={styles.aiConfirmBody}>
              <div className={styles.aiConfirmIconWrap}>
                <AutoAwesomeRoundedIcon />
              </div>

              <h4 className={styles.aiConfirmTitle}>Review AI-generated content</h4>

              <p className={styles.aiConfirmText}>
                AI-assisted features are experimental and can sometimes be inaccurate.
                Please double-check the output before you proceed.
              </p>
            </div>

            <div className={styles.aiConfirmFooter}>
              <button
                className={styles.aiCancelBtn}
                onClick={() => setShowAiSaveConfirm(false)}
                disabled={savingTemplate}
              >
                Cancel
              </button>

              <button
                className={styles.aiProceedBtn}
                onClick={saveTemplate}
                disabled={savingTemplate}
              >
                {savingTemplate ? "Saving..." : "Yes, Proceed"}
              </button>
            </div>
          </div>
        </div>
      )}

       {showGallery && (
    <aside className={styles.gallerySidebar}>
      <ImageGalleryV2
        showGallery={showGallery}
        onClose={() => setShowGallery(false)}
        onSelectImage={(url: string) => {
          const editor =
            window.CKEDITOR?.instances?.[activeEditorId || AI_EDITOR_ID];
          if (!editor) return;

          editor.insertHtml(`<img src="${url}" alt="" />`);
          setShowGallery(false);
        }}
      />
    </aside>
  )}

      {preview && (
        <div className={styles.modalOverlay} onClick={() => setPreview(null)}>
          <div
            className={`${styles.modalContent} ${styles.previewModal}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2>Preview: {preview.name}</h2>
              <button className={styles.closeBtn} onClick={() => setPreview(null)}>
                ✕
              </button>
            </div>

            <div className={styles.modalBody}>
              <div
                className={styles.previewBody}
                dangerouslySetInnerHTML={{ __html: preview.body }}
              />
            </div>

            <div className={styles.modalFooter}>
              <button
                className={styles.closeBtnOutlined}
                onClick={() => setPreview(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Templates;